import { useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import { IconoCerrar, IconoCheck, IconoMas } from '../../componentes/Iconos';
import { Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import type { Hito, ProyectoDetalle } from '../../lib/consultas';
import { useActualizarHito, useAlternarHito, useCrearHito, useEliminarHito } from '../../lib/mutaciones';

/**
 * Hitos y entregables.
 *
 * Un hito marcado como facturable no es solo una casilla: es lo que genera una
 * cuota en el cronograma de cobranza. Por eso al cambiar uno se regenera el
 * cronograma en el backend, y la ficha se refresca sola.
 */
export function PestanaHitos({ proyecto }: { proyecto: ProyectoDetalle }) {
  const [editando, setEditando] = useState<Hito | null>(null);
  const [creando, setCreando] = useState(false);

  const avisos = useAvisos();
  const alternar = useAlternarHito();
  const eliminar = useEliminarHito();

  const completados = proyecto.hitos.filter((hito) => hito.completado).length;
  const facturables = proyecto.hitos.filter((hito) => hito.esFacturable);
  const totalFacturable = facturables.reduce((suma, hito) => suma + (hito.montoFacturable ?? 0), 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {completados} de {proyecto.hitos.length} completados
          {facturables.length > 0 && (
            <>
              {' · '}
              {facturables.length} facturable(s) por{' '}
              <span className="td-mono" style={{ color: 'var(--accent-green)' }}>
                {formatearCLP(totalFacturable)}
              </span>
            </>
          )}
        </div>

        <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>
          <IconoMas size={13} />
          Agregar hito
        </button>
      </div>

      {proyecto.hitos.length === 0 ? (
        <div className="panel">
          <Vacio
            titulo="Sin hitos definidos"
            detalle="Los hitos marcan los entregables del proyecto. Si además los marcas como facturables, generan las cuotas del cronograma de cobranza."
            accion={
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>
                <IconoMas size={13} />
                Definir el primero
              </button>
            }
          />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {proyecto.hitos.map((hito) => (
            <div
              key={hito.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 16px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <button
                type="button"
                onClick={() => void alternar.mutateAsync(hito.id)}
                aria-label={hito.completado ? `Marcar ${hito.nombre} como pendiente` : `Completar ${hito.nombre}`}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  flexShrink: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1.5px solid ${hito.completado ? 'var(--accent-green)' : 'var(--border-medium)'}`,
                  background: hito.completado ? 'var(--accent-green)' : 'transparent',
                  color: '#fff',
                }}
              >
                {hito.completado && <IconoCheck size={13} />}
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: hito.completado ? 'var(--text-muted)' : 'var(--text-primary)',
                    textDecoration: hito.completado ? 'line-through' : undefined,
                  }}
                >
                  {hito.nombre}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {hito.fechaPlanificada && <span>Planificado: {hito.fechaPlanificada.slice(0, 10)}</span>}
                  {hito.fechaReal && <span>Real: {hito.fechaReal.slice(0, 10)}</span>}
                  {hito.descripcion && <span>{hito.descripcion}</span>}
                </div>
              </div>

              {hito.esFacturable && (
                <span
                  className="badge"
                  style={{ background: 'var(--accent-green-soft)', color: 'var(--accent-green)', flexShrink: 0 }}
                >
                  {hito.montoFacturable ? formatearCLP(hito.montoFacturable) : 'facturable'}
                </span>
              )}

              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditando(hito)}>
                  Editar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    try {
                      await eliminar.mutateAsync(hito.id);
                      avisos.exito('Hito eliminado');
                    } catch (error) {
                      avisos.error(error instanceof Error ? error.message : 'No se pudo eliminar.');
                    }
                  }}
                  style={{ color: 'var(--accent-red)' }}
                  aria-label={`Eliminar ${hito.nombre}`}
                >
                  <IconoCerrar size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creando || editando) && (
        <DialogoHito
          proyecto={proyecto}
          hito={editando}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
        />
      )}
    </>
  );
}

function DialogoHito({
  proyecto,
  hito,
  alCerrar,
}: {
  proyecto: ProyectoDetalle;
  hito: Hito | null;
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const crear = useCrearHito();
  const actualizar = useActualizarHito();

  const [nombre, setNombre] = useState(hito?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(hito?.descripcion ?? '');
  const [fechaPlanificada, setFechaPlanificada] = useState(hito?.fechaPlanificada?.slice(0, 10) ?? '');
  const [esFacturable, setEsFacturable] = useState(hito?.esFacturable ?? false);
  const [montoFacturable, setMontoFacturable] = useState(hito?.montoFacturable ?? 0);

  const guardando = crear.isPending || actualizar.isPending;

  const guardar = async () => {
    const datos = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || undefined,
      fechaPlanificada: fechaPlanificada || null,
      esFacturable,
      montoFacturable: esFacturable && montoFacturable > 0 ? montoFacturable : null,
    };

    try {
      if (hito) {
        await actualizar.mutateAsync({ id: hito.id, ...datos });
        avisos.exito('Hito actualizado');
      } else {
        await crear.mutateAsync({ proyectoId: proyecto.id, ...datos });
        avisos.exito('Hito agregado');
      }
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo guardar el hito.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{hito ? 'Editar hito' : 'Agregar hito'}</h2>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group ancho-total">
              <label htmlFor="nombreHito">Nombre</label>
              <input id="nombreHito" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: entrega del levantamiento" autoFocus />
            </div>

            <div className="form-group ancho-total">
              <label htmlFor="descHito">Descripción</label>
              <input id="descHito" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Opcional" />
            </div>

            <div className="form-group">
              <label htmlFor="fechaHito">Fecha planificada</label>
              <input id="fechaHito" type="date" value={fechaPlanificada} onChange={(e) => setFechaPlanificada(e.target.value)} />
            </div>

            <div className="form-group">
              <label htmlFor="facturable">¿Genera cobro?</label>
              <label
                htmlFor="facturable"
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer', paddingTop: 10 }}
              >
                <input
                  id="facturable"
                  type="checkbox"
                  checked={esFacturable}
                  onChange={(e) => setEsFacturable(e.target.checked)}
                  style={{ accentColor: 'var(--accent-green)' }}
                />
                Facturable
              </label>
            </div>

            {esFacturable && (
              <div className="form-group ancho-total">
                <label htmlFor="montoHito">Monto a facturar (neto)</label>
                <input id="montoHito" type="number" value={montoFacturable || ''} onChange={(e) => setMontoFacturable(Number(e.target.value))} min={0} />
                <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                  Si lo dejas vacío, el cronograma reparte el saldo del contrato entre los hitos facturables sin monto.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void guardar()} disabled={nombre.trim().length === 0 || guardando}>
            {guardando ? 'Guardando…' : hito ? 'Guardar cambios' : 'Agregar hito'}
          </button>
        </div>
      </div>
    </div>
  );
}
